import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, CreditCard, Clock, DollarSign } from 'lucide-react';
import { dashboardService } from '../../services/dashboardService';
import StatsCard from './components/StatsCard';
import MembershipGrowthChart from './components/MembershipGrowthChart';
import GymOverview from './components/GymOverview';
import MembershipDistribution from './components/MembershipDistribution';
import VerificationRequestsTable from './components/VerificationRequestsTable';
import QuickActionsPanel from './components/QuickActionsPanel';


const Dashboard: React.FC = () => {
  // Fetch dashboard stats
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: () => dashboardService.getStats(),
    refetchInterval: 60000, // Auto-refresh every 60 seconds
  });

  // Fetch membership growth
  const { data: growthData, isLoading: growthLoading } = useQuery({
    queryKey: ['membershipGrowth'],
    queryFn: () => dashboardService.getMembershipGrowth(),
    refetchInterval: 60000,
  });

  // Fetch gym info
  const { data: gymInfo, isLoading: gymInfoLoading } = useQuery({
    queryKey: ['gymInfo'],
    queryFn: () => dashboardService.getGymInfo(),
  });

  // Fetch membership distribution
  const { data: distribution, isLoading: distributionLoading } = useQuery({
    queryKey: ['membershipDistribution'],
    queryFn: () => dashboardService.getMembershipDistribution(),
    refetchInterval: 60000,
  });

  // Fetch verification requests
  const { data: verifications, isLoading: verificationsLoading } = useQuery({
    queryKey: ['verificationRequests'],
    queryFn: () => dashboardService.getVerificationRequests(5),
    refetchInterval: 60000,
  });

 

  const handleVerificationClick = (requestId: string) => {
    console.log('Navigate to verification:', requestId);
    // TODO: Navigate to verification details page
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div >
        <h1 className="text-4xl font-bold text-[#23F47C] mb-2">Dashboard</h1>
        <p className="text-gray-400">Welcome back! Here's what's happening today.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          icon={<Users size={24} />}
          label="Total Members"
          value={stats?.totalMembers || 0}
          isLoading={statsLoading}
        />
        <StatsCard
          icon={<CreditCard size={24} />}
          label="Active Subscriptions"
          value={stats?.activeSubscriptions || 0}
          isLoading={statsLoading}
        />
        <StatsCard
          icon={<Clock size={24} />}
          label="Pending Verifications"
          value={stats?.pendingVerifications || 0}
          isLoading={statsLoading}
        />
        <StatsCard
          icon={<DollarSign size={24} />}
          label="Monthly Revenue"
          value={`$${stats?.monthlyRevenue || 0}`}
          isLoading={statsLoading}
        />
      </div>

      {/* Charts and Info Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <MembershipGrowthChart 
            data={growthData || []} 
            isLoading={growthLoading}
          />
        </div>
        <div className="space-y-6">
          <GymOverview 
            gymInfo={gymInfo || null} 
            isLoading={gymInfoLoading}
          />
          <MembershipDistribution 
            distribution={distribution || null} 
            isLoading={distributionLoading}
          />
        </div>
      </div>

      {/* Verification Requests and Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <VerificationRequestsTable
            requests={verifications || []}
            isLoading={verificationsLoading}
            onRowClick={handleVerificationClick}
          />
        </div>
        <div className="space-y-6">
          <QuickActionsPanel />
         
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
